/** How the itinerary reads in the Changes ledger and the history (plan §3 "Ledger rows"):
 *
 *  - one group per day, in the draft's order: "Itinerary · Day 3 — 2 stops changed" (the
 *    day's own fields, its stops' and their activity rows'; a row no day holds any more
 *    reads under "Itinerary");
 *  - row labels name the row: "Day · Title", "10:00 Cliffs of Moher · Title",
 *    "10:00 · Cliffs of Moher entry · Min price"; a new row is NEW-<n> until named;
 *  - membership lists read as counts with what joined and left ("3 stops (+ NEW-2,
 *    − 13:00 Doolin)"), a pin as its summary, a route file as its file, HTML as its text;
 *  - reverting a list also reverts what it carried: the entries of the new rows it added
 *    (and their children), and — for a saved row it moved in — the list it was moved out of,
 *    so a revert never leaves a saved row in no list (which would delete it on save).
 *
 *  Pure helpers the tour's editor config calls. */
import { initialDraftState, parseRecordKey, type DraftState, type DraftTarget, type RecordKey } from '@/features/records/draft/changeSet';
import type { RecordField, RecordGroup } from '@/features/records/editor/types';
import { isStagedFile } from '@/features/records/fields/widgets/stagedFiles';

import {
  ACTIVITY,
  activityRec,
  DAY,
  DAY_KML,
  DAY_SLOTS,
  dayOfRec,
  dayRec,
  DAYS_PATH,
  draftPlacement,
  htmlText,
  isItineraryRec,
  itineraryTree,
  listIn,
  newRowName,
  pinSummary,
  SLOT,
  SLOT_ACTIVITIES,
  SLOT_PIN,
  slotRec,
  TOUR_KML,
  valueIn,
  type Baselines,
} from './model';
import { activityName, dayName, slotName, type StructureNames } from './structure';

export interface ItineraryLedgerContext {
  baselines: Baselines;
  tourRec: RecordKey;
  names: StructureNames & { transportLabel: (id: unknown) => string; currencyLabel: (id: unknown) => string };
}

const notes = new WeakMap<object, Map<number, string>>();

/** "2 stops changed", "day changed · 1 stop changed". */
function dayNote(state: DraftState, ctx: ItineraryLedgerContext, dayId: number): string {
  let byDay = notes.get(state.entries);
  if (!byDay) {
    byDay = new Map();
    notes.set(state.entries, byDay);
  }
  const hit = byDay.get(dayId);
  if (hit !== undefined) return hit;
  const placement = draftPlacement(state, ctx.baselines, ctx.tourRec);
  const stops = new Set<number>();
  let dayChanged = false;
  for (const rec of Object.keys(state.entries)) {
    const { entity, id } = parseRecordKey(rec);
    if (entity === DAY && id === dayId) dayChanged = true;
    if (entity === SLOT && placement.slots.get(id)?.dayId === dayId) stops.add(id);
    if (entity === ACTIVITY) {
      const at = placement.activities.get(id);
      if (at?.dayId === dayId) stops.add(at.slotId);
    }
  }
  const parts = [
    ...(dayChanged ? ['day changed'] : []),
    ...(stops.size > 0 ? [`${stops.size} ${stops.size === 1 ? 'stop' : 'stops'} changed`] : []),
  ];
  const note = parts.join(' · ');
  byDay.set(dayId, note);
  return note;
}

export function itineraryGroupOf(rec: RecordKey, state: DraftState | undefined, ctx: ItineraryLedgerContext): RecordGroup | null {
  if (!isItineraryRec(rec)) return null;
  if (!state) return { id: 'itinerary', title: 'Itinerary' };
  const at = dayOfRec(rec, draftPlacement(state, ctx.baselines, ctx.tourRec));
  if (!at) return { id: 'itinerary', title: 'Itinerary' };
  const note = dayNote(state, ctx, at.dayId);
  return { id: `itinerary-day:${at.dayId}`, title: `Itinerary · Day ${at.dayIndex + 1}`, ...(note ? { note } : {}) };
}

function structureCtx(state: DraftState | undefined, ctx: ItineraryLedgerContext) {
  return { state: state ?? initialDraftState, baselines: ctx.baselines, tourRec: ctx.tourRec, names: ctx.names };
}

function slotLabel(state: DraftState | undefined, ctx: ItineraryLedgerContext, slotId: number): string {
  const s = structureCtx(state, ctx);
  const time = valueIn(s.state.entries, ctx.baselines, slotRec(slotId), 'slot.time');
  const title = valueIn(s.state.entries, ctx.baselines, slotRec(slotId), 'slot.title');
  const name = typeof title === 'string' && title.trim() ? title.trim() : slotId < 0 ? newRowName(slotId) : slotName(s, slotId);
  return `${typeof time === 'string' && time ? `${time} ` : ''}${name}`;
}

export function itineraryRowLabel(
  rec: RecordKey,
  path: string,
  field: RecordField | undefined,
  state: DraftState,
  ctx: ItineraryLedgerContext,
): string | undefined {
  const label = field?.label ?? path;
  if (rec === ctx.tourRec) {
    if (path === DAYS_PATH) return 'Itinerary · Days';
    if (path === TOUR_KML) return 'Itinerary · Tour route (KML)';
    return undefined;
  }
  const { entity, id } = parseRecordKey(rec);
  if (entity === DAY) return `Day · ${label}`;
  if (entity === SLOT) return `${slotLabel(state, ctx, id)} · ${label}`;
  if (entity === ACTIVITY) {
    const s = structureCtx(state, ctx);
    const at = draftPlacement(s.state, ctx.baselines, ctx.tourRec).activities.get(id);
    const time = at ? valueIn(s.state.entries, ctx.baselines, slotRec(at.slotId), 'slot.time') : null;
    const name = id < 0 && typeof valueIn(s.state.entries, ctx.baselines, rec, 'activity.activity') !== 'number' ? newRowName(id) : activityName(s, id);
    return `${typeof time === 'string' && time ? `${time} · ` : ''}${name} · ${label}`;
  }
  return undefined;
}

function plural(n: number, one: string, many: string) {
  return `${n} ${n === 1 ? one : many}`;
}

function membership(
  now: unknown,
  was: unknown,
  noun: [string, string],
  name: (id: number) => string,
): string {
  const a = Array.isArray(now) ? (now as number[]) : [];
  const b = Array.isArray(was) ? (was as number[]) : [];
  const count = plural(a.length, noun[0], noun[1]);
  const added = a.filter((id) => !b.includes(id)).map((id) => `+ ${name(id)}`);
  const removed = b.filter((id) => !a.includes(id)).map((id) => `− ${name(id)}`);
  const parts = [...added, ...removed];
  if (parts.length > 0) return `${a.length} (${parts.join(', ')})`;
  return JSON.stringify(a) === JSON.stringify(b) || noun[0] === 'stop' ? count : `${count}, reordered`;
}

/** A value as the ledger and the history read it (undefined: the generic format). */
export function itineraryFormat(field: RecordField, rec: RecordKey, value: unknown, ctx: ItineraryLedgerContext): string | undefined {
  const stored = ctx.baselines[rec]?.[field.key];
  const s = structureCtx(undefined, ctx);
  switch (field.key) {
    case DAYS_PATH:
      return membership(value, stored, ['day', 'days'], (id) => (id < 0 ? newRowName(id) : dayName(s, id)));
    case DAY_SLOTS:
      return membership(value, stored, ['stop', 'stops'], (id) => (id < 0 ? newRowName(id) : slotLabel(undefined, ctx, id)));
    case SLOT_ACTIVITIES:
      return membership(value, stored, ['activity', 'activities'], (id) => (id < 0 ? newRowName(id) : activityName(s, id)));
    case SLOT_PIN:
      return pinSummary(value, null) === 'Numbered' ? 'Numbered, in order' : pinSummary(value, null);
    case DAY_KML:
    case TOUR_KML:
      if (isStagedFile(value)) return `${value.name} (new file)`;
      return value ? 'Current file' : 'No file';
    case 'activity.activity':
      return value == null ? 'No activity' : ctx.names.activityLabel(value);
    case 'activity.currency':
      return value == null ? 'No currency' : ctx.names.currencyLabel(value);
    case 'slot.next_transport':
      return value == null ? 'None' : ctx.names.transportLabel(value);
    case 'day.description':
    case 'slot.description':
      return htmlText(value) || '—';
    default:
      return undefined;
  }
}

/** What reverting an itinerary list also reverts (see the module note). */
export function itineraryCascade(rec: RecordKey, path: string, state: DraftState, ctx: ItineraryLedgerContext): DraftTarget[] {
  const entry = state.entries[rec]?.[path];
  if (!entry) return [];
  const added = (Array.isArray(entry.to) ? (entry.to as number[]) : []).filter(
    (id) => !(Array.isArray(entry.from) ? (entry.from as number[]) : []).includes(id),
  );
  const out: DraftTarget[] = [];
  const all = (r: RecordKey) => Object.keys(state.entries[r] ?? {}).map((p) => ({ rec: r, path: p }));
  const activityTargets = (id: number) => all(activityRec(id));
  const slotTargets = (id: number): DraftTarget[] => [
    ...all(slotRec(id)),
    ...(id < 0 ? listIn(state.entries, ctx.baselines, slotRec(id), SLOT_ACTIVITIES).flatMap(activityTargets) : []),
  ];
  const dayTargets = (id: number): DraftTarget[] => [
    ...all(dayRec(id)),
    ...(id < 0 ? listIn(state.entries, ctx.baselines, dayRec(id), DAY_SLOTS).flatMap(slotTargets) : []),
  ];
  /** The saved members moved in here came out of another list of the same kind: revert it. */
  const partners = (entity: string, listPath: string, ids: number[]) => {
    for (const [r, paths] of Object.entries(state.entries)) {
      if (r === rec || parseRecordKey(r).entity !== entity) continue;
      const e = paths[listPath];
      if (!e) continue;
      const from = Array.isArray(e.from) ? (e.from as number[]) : [];
      const to = Array.isArray(e.to) ? (e.to as number[]) : [];
      if (ids.some((id) => from.includes(id) && !to.includes(id))) out.push({ rec: r, path: listPath });
    }
  };
  if (rec === ctx.tourRec && path === DAYS_PATH) {
    for (const id of added.filter((x) => x < 0)) out.push(...dayTargets(id));
  } else if (path === DAY_SLOTS) {
    for (const id of added) out.push(...(id < 0 ? slotTargets(id) : []));
    partners(DAY, DAY_SLOTS, added.filter((x) => x > 0));
  } else if (path === SLOT_ACTIVITIES) {
    for (const id of added) out.push(...(id < 0 ? activityTargets(id) : []));
    partners(SLOT, SLOT_ACTIVITIES, added.filter((x) => x > 0));
  }
  return out;
}

/** The itinerary's records in tree order (stored), for the ledger's record order. */
export function itineraryRecordOrder(baselines: Baselines, tourRec: RecordKey): RecordKey[] {
  const out: RecordKey[] = [];
  for (const day of itineraryTree(null, baselines, tourRec)) {
    out.push(dayRec(day.id));
    for (const slot of day.slots) {
      out.push(slotRec(slot.id));
      for (const a of slot.activities) out.push(activityRec(a));
    }
  }
  return out;
}
